import React, { useState } from 'react';
import classNames from 'classnames';
import _range from 'lodash/range';
import _sortBy from 'lodash/sortBy';
import _uniqBy from 'lodash/uniqBy';
import dynamic from 'next/dynamic';
import { v4 } from 'uuid';

import Table from 'components/customTable';

import ZpFilter from '../../../components/zpFilter';
import { FILTER_PARAMS, FILTER_TYPES } from '../../../components/zpFilter/filterUtils';
import CommonService from '../../../services/commonService';
import { JOB_BUCKET_GROUP } from '../../../utils/constant';

import ChartTable from './chartTable';

import classes from './lgbtRatioComparision.module.css';

const ReactTooltip = dynamic(() => import('react-tooltip'), { ssr: false });

const filterGroup = JOB_BUCKET_GROUP.CAREER_DEMOGRAPHICS_COMPARE_LGBT;

const heads = [
  { title: 'Job Title', value: 'layTitle' },
  { title: 'LGBT', value: 'lgbtAdjustedRatio' },
  { title: 'Job Openings', value: 'jobCount' },
];

const LgbtRatioComparision = ({
  jobTitle,
  lgbt,
  lgbtSkewTitles,
  contentBlurb,
  filterValueRange,
  filterLogEventCallback,
}) => {
  const tableData = (lgbtSkewTitles ?? []).map((x) => ({
    ...x,
    lgbtAdjustedRatioDisplay: (x.lgbtAdjustedRatio * 100).toFixed(2) + '%',
  }));

  function getInitialBarChartData(newData = []) {
    return _uniqBy(
      _sortBy(
        [
          {
            selected: true,
            layTitle: jobTitle,
            lgbtAdjustedRatio: lgbt.lgbtAdjustedRatio,
          },
          ...lgbtSkewTitles,
          ...newData,
        ],
        ['lgbtAdjustedRatio']
      ),
      'layTitle'
    ).reverse();
  }

  const [barChartData, setBarChartData] = useState(getInitialBarChartData());

  function calculateXLabels() {
    const last = barChartData[0].lgbtAdjustedRatio;
    const stepSize = last / 4;
    const max = last + 2 * stepSize;
    const range = _range(0, max, stepSize);
    return [range[range.length - 1], range];
  }

  const [max, xLabels] = calculateXLabels();

  const handleFilterUpdate = async (_, { value }) => {
    const lgbtJobTitle = value && value.originalSearch ? value.originalSearch : value;

    try {
      if (!lgbtJobTitle || !lgbtJobTitle.length) {
        setBarChartData(getInitialBarChartData());
        return;
      }

      const jobTitles = [];

      const existingJobTitles = [];

      const index = barChartData.findIndex((data) => data.layTitle === lgbtJobTitle);
      if (index === -1) {
        jobTitles.push(lgbtJobTitle);
      } else {
        existingJobTitles.push(lgbtJobTitle);
      }

      if (!jobTitles.length) return;

      const data = { jobTitles };

      let newBarChartData = await CommonService.fetchLgbtRatioData(data);

      newBarChartData = newBarChartData.map((data) => ({
        ...data,
        selected: true,
      }));

      if (existingJobTitles.length) {
        existingJobTitles.forEach((normalizedTitle) => {
          const index = barChartData.findIndex((data) => data.layTitle === normalizedTitle);
          if (index !== -1) {
            newBarChartData.push(barChartData[index]);
          }
        });
      }

      newBarChartData = getInitialBarChartData(newBarChartData);

      setBarChartData(newBarChartData);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <section id="lgbt-ratio-comparision">
      <h3 className="cmpSectionh4 z-mb-24 text-capitalize">
        {`${jobTitle} LGBT ratio compared to similar jobs`}
      </h3>

      {contentBlurb && contentBlurb.content && (
        <div dangerouslySetInnerHTML={{ __html: contentBlurb.content }} />
      )}

      <div className="mt-4">
        <ZpFilter
          onApply={handleFilterUpdate}
          onClear={handleFilterUpdate}
          type={FILTER_TYPES.SINGLE}
          param={FILTER_PARAMS.JOB_TITLE}
          buttonText="Compare Jobs"
          valueRange={filterValueRange}
          options={{
            logEvent: () => filterLogEventCallback(filterGroup),
            autoSuggest: true,
          }}
        />
      </div>

      <div className="mt-4">
        <div className="d-block d-md-none">
          <ChartTable
            heads={heads}
            tableData={barChartData}
            defaultSortedField="lgbtAdjustedRatio"
            formatter={(value, title) =>
              title !== heads[1]?.title ? value : `${(value * 100).toFixed(2)}%`
            }
          />
        </div>
        <div className={classNames('d-none d-md-block', classes['h-bar-container'])}>
          <div className={classNames('d-inline-flex flex-column', classes['y-labels'])}>
            {barChartData.map(({ layTitle, lgbtAdjustedRatio, selected }, idx) => (
              <div key={v4()} className={classes['y-label']}>
                <div
                  className={classNames(classes['h-bar'], { [classes.selected]: selected })}
                  style={{ width: `calc((100% - 218px) * ${lgbtAdjustedRatio / max})` }}
                  data-for={`lgbtRatio-${layTitle}-${idx}`}
                  data-tip
                />
                <ReactTooltip id={`lgbtRatio-${layTitle}-${idx}`} place="top">
                  <p className="mb-0 text-white">
                    {layTitle}: <strong>{(100 * lgbtAdjustedRatio).toFixed(2)}%</strong>
                  </p>
                </ReactTooltip>
                <label className="openSansBlack">{layTitle}</label>
              </div>
            ))}
          </div>
          <div className={classNames('d-inline-flex', classes['x-labels'])}>
            {xLabels.map((xLabel, idx) => (
              <div key={xLabel + idx} className={classes['x-label']}>
                <label className="lightTextStyle2">{`${Math.round(xLabel * 100)}%`}</label>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="mb-0 z-mt-98 d-none d-md-block">
        <Table
          columnConfig={{
            layTitle: {
              columnName: 'Profession',
              isLink: 'isIndex',
              linkUrl: 'link',
            },
            lgbtAdjustedRatioDisplay: {
              columnName: 'Percentages of LGBT ',
              customSortByField: 'lgbtAdjustedRatio',
              // defaultSortDirection: 'desc'
            },
            jobCount: {
              columnName: 'Job Openings',
            },
          }}
          bodyData={tableData}
          defaultRowsDisplay={99}
          tableHeaderTitleContent={
            <h3 className="zp-title-h3 text-capitalize z-mb-24">{`Similar ${jobTitle?.toLowerCase?.()} jobs LGBT demographics`}</h3>
          }
        />
      </div>
    </section>
  );
};

export default LgbtRatioComparision;
